const STORAGE_KEY = 'genvas:video-muted';
let preferredMuted: boolean | undefined;
const players = new Set<VideoPlaybackController>();
const previews: VideoPlaybackController[] = [];

export function getVideoMuted(): boolean {
  if (preferredMuted !== undefined) return preferredMuted;
  try {
    preferredMuted = window.localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    preferredMuted = true;
  }
  return preferredMuted;
}

export function setVideoMuted(muted: boolean): void {
  preferredMuted = muted;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(muted));
  } catch {}
  for (const player of players) player.applyPreference();
}

function onStorage(event: StorageEvent) {
  if (event.key !== STORAGE_KEY && event.key !== null) return;
  preferredMuted = event.newValue !== 'false';
  for (const player of players) player.applyPreference();
}

export interface VideoPlaybackController {
  play: () => void;
  pause: () => void;
  toggleMuted: () => void;
  applyPreference: () => void;
  dispose: () => void;
}

export function registerVideoPlayback(
  video: HTMLVideoElement,
  onMutedChange: (muted: boolean) => void,
  preview = false,
): VideoPlaybackController {
  return registerMediaPlayback(video, {
    preview,
    onMutedChange,
    useVideoPreference: true,
  });
}

// WaveformPlayer and video nodes share the same preview priority and lifecycle.
// Audio keeps its own volume; a video's mute preference must not silence it.
export function registerMediaPlayback(
  video: HTMLMediaElement,
  {
    preview = false,
    onMutedChange,
    useVideoPreference = false,
    onPlayError,
  }: {
    preview?: boolean;
    onMutedChange?: (muted: boolean) => void;
    useVideoPreference?: boolean;
    onPlayError?: (error: unknown) => void;
  } = {},
): VideoPlaybackController {
  let disposed = false;
  let attempt = 0;
  let playPending = false;
  const notify = () => onMutedChange?.(video.muted);
  const canPlay = () =>
    !disposed && (!previews.length || previews.at(-1) === controller);

  const controller: VideoPlaybackController = {
    play() {
      if (!canPlay()) return;
      playPending = true;
      const currentAttempt = ++attempt;
      if (useVideoPreference) video.muted = getVideoMuted();
      notify();
      void (async () => {
        try {
          await video.play();
        } catch (error) {
          if (currentAttempt !== attempt || !canPlay()) return;
          onPlayError?.(error);
          if (
            !useVideoPreference ||
            video.muted ||
            (error as { name?: string } | null)?.name !== 'NotAllowedError'
          )
            return;
          // 浏览器降级只影响当前播放器，不改用户选择。
          video.muted = true;
          notify();
          try {
            await video.play();
          } catch {}
        } finally {
          if (currentAttempt === attempt) playPending = false;
        }
      })();
    },
    pause() {
      playPending = false;
      ++attempt;
      video.pause();
    },
    toggleMuted() {
      setVideoMuted(!video.muted);
    },
    applyPreference() {
      if (!useVideoPreference) return;
      const shouldPlay = playPending || !video.paused;
      ++attempt;
      playPending = false;
      video.muted = getVideoMuted();
      notify();
      if (shouldPlay) controller.play();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      controller.pause();
      video.removeEventListener('volumechange', notify);
      players.delete(controller);
      const index = previews.indexOf(controller);
      if (index >= 0) previews.splice(index, 1);
      if (!players.size) window.removeEventListener('storage', onStorage);
    },
  };

  if (!players.size) {
    try {
      preferredMuted = window.localStorage.getItem(STORAGE_KEY) !== 'false';
    } catch {}
    window.addEventListener('storage', onStorage);
  }
  players.add(controller);
  if (preview) {
    previews.push(controller);
    for (const player of players) {
      if (player !== controller) player.pause();
    }
  }
  video.addEventListener('volumechange', notify);
  controller.applyPreference();
  return controller;
}
